/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import { formatCurrency, formatDate, formatDateOnly } from '../../utils/formatters.ts';
import PayoutForm from '../PayoutForm.tsx';

interface PayoutDetailsExpandedProps {
  order: any;
  orderId: string;
  payoutData: any;
  isAdmin: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSubmitEdit: (data: any) => void;
}

const PayoutDetailsExpanded: React.FC<PayoutDetailsExpandedProps> = ({
  order,
  orderId,
  payoutData,
  isAdmin,
  isEditing,
  onEdit,
  onCancelEdit,
  onSubmitEdit,
}) => {
  if (!payoutData) {
    return (
      <div className="expanded-section payout-details-expanded">
        <h4 className="expanded-title">
          Payout Details for Order #{order.orderNumber}
        </h4>
        <div className="payout-details-section">
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            Loading payout details...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="expanded-section payout-details-expanded">
      <h4 className="expanded-title">
        Payout Details for Order #{order.orderNumber}
      </h4>
      <div className="payout-details-section">
        {isEditing ? (
          <div style={{ padding: '15px' }}>
            <PayoutForm
              orderId={orderId}
              orderNumber={order.orderNumber}
              payoutAmount={payoutData.amount}
              isAdmin={isAdmin}
              onSubmit={(data) => onSubmitEdit(data)}
              initialData={{
                date: payoutData.date,
                amount: payoutData.amount,
                method: payoutData.method,
                confirmationNumber: payoutData.confirmationNumber,
                notes: payoutData.notes
              }}
            />
            <button
              onClick={onCancelEdit}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <div className="payout-info-grid">
              <div className="payout-detail-item">
                <span className="detail-label">Payout Date:</span>
                <span className="detail-value">{formatDateOnly(payoutData.date)}</span>
              </div>
              
              <div className="payout-detail-item">
                <span className="detail-label">Amount Paid:</span>
                <span className="detail-value green" style={{  fontWeight: 'bold' }}>
                  {formatCurrency(payoutData.amount)}
                </span>
              </div>
              
              <div className="payout-detail-item">
                <span className="detail-label">Payment Method:</span>
                <span className="detail-value">{payoutData.method}</span>
              </div>
              
              {payoutData.confirmationNumber && (
                <div className="payout-detail-item">
                  <span className="detail-label">Confirmation #:</span>
                  <span className="detail-value">{payoutData.confirmationNumber}</span>
                </div>
              )}
              
              <div className="payout-detail-item">
                <span className="detail-label">Created By:</span>
                <span className="detail-value">{payoutData.createdBy || 'Admin'}</span>
              </div>
              
              <div className="payout-detail-item">
                <span className="detail-label">Created At:</span>
                <span className="detail-value">{formatDate(payoutData.createdAt)}</span>
              </div>
              
              {payoutData.updatedBy && (
                <>
                  <div className="payout-detail-item">
                    <span className="detail-label">Last Updated By:</span>
                    <span className="detail-value">{payoutData.updatedBy}</span>
                  </div>
                  
                  <div className="payout-detail-item">
                    <span className="detail-label">Updated At:</span>
                    <span className="detail-value">{formatDate(payoutData.updatedAt)}</span>
                  </div>
                </>
              )}
              
              {payoutData.notes && (
                <div className="payout-detail-item" style={{ gridColumn: '1 / -1' }}>
                  <span className="detail-label">Notes:</span>
                  <span className="detail-value" style={{ fontStyle: 'italic' }}>
                    {payoutData.notes}
                  </span>
                </div>
              )}
              
              {payoutData.orders && payoutData.orders.length > 1 && (
                <div className="payout-detail-item" style={{ gridColumn: '1 / -1' }}>
                  <span className="detail-label">Batch Payout of  {formatCurrency(payoutData.amount)} for Order(s):</span>
                  <span className="detail-value">
                    {payoutData.orders.map((order: any) => order.number).join(', ')}
                  </span>
                </div>
              )}
            </div>
            
            {isAdmin && (
              <div style={{ marginTop: '15px', textAlign: 'right' }}>
                <button
                  onClick={onEdit}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Edit Payout
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PayoutDetailsExpanded;